// IndexedDB wrapper for offline story storage
const DB_NAME = 'LegiOfflineDB';
const DB_VERSION = 1;
const STORE_STORIES = 'stories';
const STORE_AUDIO = 'audio';

class OfflineStorage {
  constructor() {
    this.db = null;
  }

  // Initialize IndexedDB
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create stories store
        if (!db.objectStoreNames.contains(STORE_STORIES)) {
          const storyStore = db.createObjectStore(STORE_STORIES, { keyPath: 'id', autoIncrement: true });
          storyStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Create audio store
        if (!db.objectStoreNames.contains(STORE_AUDIO)) {
          db.createObjectStore(STORE_AUDIO, { keyPath: 'storyId' });
        }
      };
    });
  }

  // Save a story
  async saveStory(story) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_STORIES], 'readwrite');
      const store = transaction.objectStore(STORE_STORIES);

      const storyData = {
        ...story,
        timestamp: Date.now()
      };

      const request = store.add(storyData);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Save audio blob
  async saveAudio(storyId, audioBlob) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_AUDIO], 'readwrite');
      const store = transaction.objectStore(STORE_AUDIO);

      const request = store.put({ storyId, blob: audioBlob });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get oldest story (FIFO)
  async getOldestStory() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_STORIES], 'readonly');
      const store = transaction.objectStore(STORE_STORIES);
      const index = store.index('timestamp');

      const request = index.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          resolve(cursor.value);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get audio for story
  async getAudio(storyId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_AUDIO], 'readonly');
      const store = transaction.objectStore(STORE_AUDIO);

      const request = store.get(storyId);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.blob);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Delete story by id
  async deleteStory(id) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_STORIES], 'readwrite');
      const store = transaction.objectStore(STORE_STORIES);

      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Delete audio
  async deleteAudio(storyId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_AUDIO], 'readwrite');
      const store = transaction.objectStore(STORE_AUDIO);

      const request = store.delete(storyId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get count of stored stories
  async getStoryCount() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_STORIES], 'readonly');
      const store = transaction.objectStore(STORE_STORIES);

      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Clear all offline data
  async clearAll() {
    if (!this.db) await this.init();

    return Promise.all([
      new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_STORIES], 'readwrite');
        const request = transaction.objectStore(STORE_STORIES).clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_AUDIO], 'readwrite');
        const request = transaction.objectStore(STORE_AUDIO).clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
  }
}

export default new OfflineStorage();
